import { Message, MessageType } from "../models/Message";

export interface LocationMessageContent {
  latitude?: number;
  longitude?: number;
  name?: string;
  address?: string;
  url?: string;
  comment?: string;
  isLive?: boolean;
  accuracyInMeters?: number;
};

export interface ContactMessageContent {
  displayName?: string;
  vcard?: string;
  isSelf?: boolean;
};

export interface PollOptionContent {
  label: string;
  votes?: number;
  selected?: boolean;
};

export interface PollMessageContent {
  title?: string;
  options?: Array<string | PollOptionContent>;
  selectableOptionCount?: number;
};

const parseJSON = ( value: string ) => {
  try {
    return JSON.parse( value || "{}" );
  } catch {
    return {};
  }
};

const hasCoordinate = ( value: unknown ): value is number => typeof value === "number" && Number.isFinite( value );

const extractVCardField = ( vcard: string | undefined, fieldName: string ) => {
  if( !vcard ) return "";

  const prefix = `${ fieldName.toUpperCase() }`;
  const line = vcard
    .split(/\r?\n/)
    .find(( item ) => item.trim().toUpperCase().startsWith( prefix ));

  const value = line?.split( ":" ).slice( 1 ).join( ":" ).trim() ?? "";

  return value.replace(/\\,/g, ",").replace(/\\;/g, ";");
};

export const parseLocationMessage = ( message: Message ): LocationMessageContent | null => {
  if( message.messageType !== MessageType.LOCATION && message.messageType !== MessageType.LIVE_LOCATION ) return null;

  const parsed = parseJSON( message.content );
  const location = parsed.location ?? parsed;

  return location && typeof location === "object" ? location : null;
};

export const parsePollMessage = ( message: Message ): PollMessageContent | null => {
  if( message.messageType !== MessageType.POLL ) return null;

  const parsed = parseJSON( message.content );
  const poll = parsed.poll ?? parsed;

  return poll && typeof poll === "object" ? poll : null;
};

export const normalizePollOptions = ( poll: PollMessageContent | null ): PollOptionContent[] => {
  const options = poll?.options;
  if( !Array.isArray( options )) return [];

  return options.reduce<PollOptionContent[]>(( result, option ) => {
      if( typeof option === "string" ) {
        if( option ) result.push({
          label: option,
          votes: 0,
          selected: false,
        });

        return result;
      }

      if( option && typeof option === "object" ) {
        const label = option.label || "";
        const votes = typeof option.votes === "number" && Number.isFinite( option.votes ) ? Math.max( 0, option.votes ) : 0;

        if( label ) {
          result.push({
            label,
            votes,
            selected: Boolean( option.selected ),
          });
        }
      }

      return result;
    }, []);
};

export const parseContactMessage = ( message: Message ): ContactMessageContent[] => {
  if( message.messageType !== MessageType.CONTACT ) return [];

  const parsed = parseJSON( message.content );
  const contacts = parsed.contacts ?? ( parsed.contact ? [ parsed.contact ] : [] );

  return Array.isArray( contacts ) ? contacts : [];
};

export const formatLocationCoordinates = ( location: LocationMessageContent | null ) => {
  if( !location || !hasCoordinate( location.latitude ) || !hasCoordinate( location.longitude )) return "";

  return `${ location.latitude.toFixed( 6 ) }, ${ location.longitude.toFixed( 6 )}`;
};

export const buildLocationMapURL = ( location: LocationMessageContent | null ) => {
  if( !location ) return "";
  if( location.url ) return location.url;
  if( !hasCoordinate( location.latitude ) || !hasCoordinate( location.longitude )) return "";

  return `https://www.google.com/maps/search/?api=1&query=${ encodeURIComponent(`${ location.latitude },${ location.longitude }`) }`;
};

export const getContactDisplayName = ( contact: ContactMessageContent | undefined ) => {
  if( !contact ) return "Contato compartilhado";

  return contact.displayName || extractVCardField( contact.vcard, "FN" ) || "Contato compartilhado";
};

export const getContactPhones = ( contact: ContactMessageContent | undefined ) => {
  if( !contact?.vcard ) return [];

  return contact.vcard
    .split(/\r?\n/)
    .filter(( line ) => line.trim().toUpperCase().startsWith( "TEL" ))
    .map(( line ) => line.split( ":" ).slice( 1 ).join( ":" ).trim())
    .filter(Boolean);
};

export const getSpecialMessagePreview = ( message: Message ) => {
  if( message.messageType === MessageType.LOCATION || message.messageType === MessageType.LIVE_LOCATION ) {
    const location = parseLocationMessage( message );
    const label = location?.name || location?.address || location?.comment;
    const coordinates = formatLocationCoordinates( location );
    const prefix = message.messageType === MessageType.LIVE_LOCATION || location?.isLive ? "Localização em tempo real" : "Localização";

    return label ? `${ prefix }: ${ label }` : coordinates ? `${ prefix }: ${ coordinates }` : prefix;
  }

  if( message.messageType === MessageType.CONTACT ) {
    const contacts = parseContactMessage( message );
    const first = contacts[ 0 ];
    const countSuffix = contacts.length > 1 ? ` +${ contacts.length - 1 }` : "";

    return `${ getContactDisplayName( first ) }${ countSuffix }`;
  }

  if( message.messageType === MessageType.POLL ) {
    const poll = parsePollMessage( message );
    return poll?.title ? `Enquete: ${ poll.title }` : "Enquete";
  }

  return "";
};
